const norm = (s) => (s ?? '').toString().trim().toLowerCase();

/** Resolve VAT tax row from pending requisition / item master */
export const resolveVatTax = (vatList, { vatDesc, vatPerc, itemMasterVat }) => {
  if (vatDesc && vatList?.length) {
    const target = norm(vatDesc);
    const byDesc = vatList.find((t) => norm(t?.description) === target);
    if (byDesc) return byDesc;
  }
  const rate = parseFloat(vatPerc);
  if (!Number.isNaN(rate) && vatList?.length) {
    const byRate = vatList.find((t) => Math.abs(Number(t?.rate) - rate) < 0.01);
    if (byRate) return byRate;
  }
  if (itemMasterVat && vatList?.length) {
    const target = norm(itemMasterVat);
    return vatList.find((t) => norm(t?.description) === target) || null;
  }
  return null;
};

export const getMoneyPrecision = (currencyPrecision) => {
  const p = Number(currencyPrecision);
  return Number.isFinite(p) && p >= 0 ? Math.max(p, 2) : 3;
};

/**
 * Line totals: Amount includes subtotal + VAT from API or calculated rate.
 */
export const computeLineFinancials = ({
  qty,
  unitPrice,
  vatList,
  vatDesc,
  vatPerc,
  vatAmountFromApi,
  discount = 0,
  selectedCheck,
  isDiscountPercentage,
  itemMasterVat,
  currencyPrecision = 3,
}) => {
  const q = parseFloat(qty) || 0;
  const price = parseFloat(unitPrice) || 0;
  const subtotal = q * price;
  const selectedVat = resolveVatTax(vatList, { vatDesc, vatPerc, itemMasterVat });
  const vatRate = selectedVat?.rate ?? (parseFloat(vatPerc) || 0);

  let vatAmount =
    vatAmountFromApi != null && vatAmountFromApi !== '' && !Number.isNaN(parseFloat(vatAmountFromApi))
      ? parseFloat(vatAmountFromApi)
      : (subtotal / 100) * vatRate;

  const grossWithVat = subtotal + vatAmount;
  const calculatedAmount = selectedCheck ? grossWithVat : subtotal;
  const discountNum = parseFloat(discount) || 0;

  let discountamount = 0;
  if (isDiscountPercentage) {
    discountamount = (discountNum / 100) * grossWithVat;
  } else {
    discountamount = discountNum;
  }

  const finalAmount = grossWithVat - discountamount;
  const prec = getMoneyPrecision(currencyPrecision);
  const fmt = (n) => Number(n).toFixed(prec);

  return {
    vatRate,
    vatDec: selectedVat?.description || vatDesc || '',
    vatAmount: fmt(vatAmount),
    calculatedAmount: fmt(calculatedAmount),
    discountAmount: fmt(discountamount),
    amount: fmt(finalAmount),
    discount: discountNum,
    subtotal: fmt(subtotal),
  };
};

/** Build CreatePendingRequisitionsPO request body from form + pending lines */
export const buildPendingRequisitionsPOPayload = ({
  pOrderNo,
  formData,
  items,
  pendingLines = [],
  supplier,
  supplierId,
  payment,
  currency,
  isVatExcluded,
  userName,
  contactNumber,
  supplierContact,
}) => {
  const prec = getMoneyPrecision(currency?.value?.precision ?? 3);
  const docDate = formData?.DocumentDate ? new Date(formData.DocumentDate).toISOString() : new Date().toISOString();
  const deliveryDate = formData?.deliveryDate ? new Date(formData.deliveryDate).toISOString() : docDate;
  const docId = formData?.RequisitionType?.label ?? formData?.RequisitionType ?? '';
  const roundMoney = (n) => parseFloat(Number(n ?? 0).toFixed(prec));
  const asNumber = (n) => {
    const v = parseFloat(n);
    return Number.isFinite(v) ? v : 0;
  };
  const resolveLineVatAmount = (sourceLine, item) => {
    if (sourceLine?.vatAmount != null && sourceLine.vatAmount !== '') {
      return asNumber(sourceLine.vatAmount);
    }
    if (item?.vatAmount != null && item.vatAmount !== '' && !Number.isNaN(asNumber(item.vatAmount))) {
      return asNumber(item.vatAmount);
    }
    return 0;
  };

  const poDetails = (items || []).map((item, index) => {
    const sourceLine = pendingLines[index] || {};
    const qty = asNumber(item.qty);
    const unitPrice = asNumber(sourceLine.unitPrice ?? item.baseAmount);
    const lineVatAmount = resolveLineVatAmount(sourceLine, item);
    const lineAmount = roundMoney(item.amount);
    const lineDelivery = sourceLine.deliveryDate ? new Date(sourceLine.deliveryDate).toISOString() : deliveryDate;
    const vatPerc = asNumber(sourceLine.vatPerc ?? item.vat);

    return {
      pOrderNo,
      srno: index + 1,
      itemCode: item.ItemNo || '',
      itemDescription: item.itemDescription || '',
      sItemCode: '',
      uom: item.uom || '',
      budgetCode: '',
      qty,
      cancelledQty: 0,
      returnQty: 0,
      unitPrice,
      amount: lineAmount,
      baseAmount: lineAmount,
      status: 0,
      requisitionNo: item.requisitionNo || formData?.requisitionNo || '',
      rsrno: sourceLine.srno ?? index + 1,
      deliveryDate: lineDelivery,
      vatDesc: sourceLine.vatDesc || item.vatDec || '',
      vatPerc,
      vatUnitPrice: qty > 0 ? roundMoney(lineVatAmount / qty) : 0,
      vatAmount: lineVatAmount,
      vatBaseAmount: lineVatAmount,
      specification: item.specification ?? '',
      discount: asNumber(item.discount),
    };
  });

  return {
    pOrderNo,
    docId,
    requisitionNo: formData?.requisitionNo || '',
    vendorId: supplierId?.label?.toString() ?? '',
    vendorName: supplier?.label ?? '',
    docDate,
    deliveryDate,
    location: formData?.location ?? '',
    currencyCode: currency?.label ?? '',
    exchangeRate: currency?.value?.conversionRate ?? (parseFloat(formData?.exchangeRate) || 0),
    terms: payment?.label || '',
    department: formData?.Department ?? '',
    attention: supplierContact ?? '',
    fax: formData?.lPOType?.label || '0',
    quotationRef: formData?.QuotationReq ?? '',
    contractNo: contactNumber ?? '',
    deliveryPoint: formData?.deliveryPoint ?? '',
    contactPerson: formData?.contactPerson ?? '',
    aprrovalUser: '',
    description: formData?.Description ?? '',
    remarks: formData?.Remarks ?? '',
    user: userName ?? '',
    costCenter1: formData?.costCenter1?.label ?? '',
    costCenter2: formData?.costCenter2?.label ?? '',
    costCenter3: formData?.costCenter3?.label ?? '',
    costCenter4: formData?.costCenter4?.label ?? '',
    costCenter5: isVatExcluded === true ? 'excluded' : 'Included',
    discountPerc: 0,
    poType: formData?.OrderType?.label || 'POrder',
    contractedValue: 0,
    poDetails,
  };
};

/** Map pending requisition API rows to save-ready line items */
export const pendingLinesToSaveItems = (lines, { selectedCheck, isDiscountPercentage, currencyPrecision, vatList }) =>
  (lines || []).map((line, index) => {
    const qty = parseFloat(line?.balQty ?? line?.baseQty) || 0;
    const price = parseFloat(line?.unitPrice) || 0;
    const fin = computeLineFinancials({
      qty,
      unitPrice: price,
      vatList,
      vatDesc: line?.vatDesc,
      vatPerc: line?.vatPerc,
      vatAmountFromApi: line?.vatAmount,
      discount: 0,
      selectedCheck,
      isDiscountPercentage,
      currencyPrecision,
    });
    const vatFromApi = line?.vatAmount != null && line?.vatAmount !== '' ? parseFloat(line.vatAmount) : parseFloat(fin.vatAmount);

    return {
      requisitionNo: line?.requisitionNo,
      srno: line?.srno ?? index + 1,
      ItemNo: line?.itemCode,
      itemDescription: line?.itemDescription,
      qty,
      uom: line?.baseUOM,
      baseQty: parseFloat(line?.baseQty) || 1,
      specification: line?.specification ?? '',
      baseAmount: price,
      amount: parseFloat(fin.amount),
      vatAmount: Number.isFinite(vatFromApi) ? vatFromApi : 0,
      vat: fin.vatRate,
      vatDec: fin.vatDec,
      discount: fin.discount,
    };
  });
